import { useCallback, useState } from "react";
import { useTranslations } from "next-intl";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

type RebuildCourse = { id: string; title: string };

type RebuildResult = { queued: boolean; built?: boolean };

/**
 * COMM-2 follow-on: "Rebuild AI tutor index" for course staff. The tutor also rebuilds on its own
 * when lesson content changes; this is the manual "do it now". The server allows only staff
 * assigned to the course (or the tenant admin).
 */
export function AiTutorRebuildPanel({ courses }: { courses: RebuildCourse[] }) {
  const t = useTranslations("aiTutor");
  const tRoot = useTranslations();
  const [courseId, setCourseId] = useState(courses[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const errorText = useCallback(
    (err: unknown) =>
      apiErrorText(err, tRoot, "aiTutor.rebuildFailed", { namespace: "aiTutor.rebuildErrors" }),
    [tRoot],
  );

  if (courses.length === 0) return null;
  const selectedId = courses.some((course) => course.id === courseId) ? courseId : courses[0]!.id;

  async function rebuild() {
    setBusy(true);
    setStatus(null);
    setError(null);
    try {
      const res = await apiFetch(`/v1/instructor/courses/${selectedId}/ai-tutor`, {
        method: "POST",
      });
      if (!res.ok) throw await readApiErrorResponse(res);
      const json = (await res.json()) as { data: RebuildResult };
      setStatus(json.data.queued ? t("rebuildQueued") : t("rebuildDone"));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      className="rounded-xl border border-[var(--border)] p-5"
      data-testid="ai-rebuild-panel"
    >
      <h2 className="text-lg font-semibold">{t("rebuildTitle")}</h2>
      <p className="mt-1 text-sm text-[var(--text-muted)]">{t("rebuildHint")}</p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t("rebuildCourseLabel")}</span>
          <select
            value={selectedId}
            onChange={(event) => setCourseId(event.target.value)}
            className="w-full max-w-md rounded-md border border-[var(--border-strong)] px-3 py-2"
            data-testid="ai-rebuild-course"
          >
            {courses.map((course) => (
              <option key={course.id} value={course.id}>
                {course.title}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => void rebuild()}
          disabled={busy}
          className="rounded bg-[var(--ink-9)] px-4 py-2 text-sm text-white disabled:opacity-50"
          data-testid="ai-rebuild-submit"
        >
          {busy ? t("rebuilding") : t("rebuildIndex")}
        </button>
      </div>
      {status ? (
        <p role="status" className="mt-3 text-sm" data-testid="ai-rebuild-status">
          {status}
        </p>
      ) : null}
      {error ? (
        <p
          role="alert"
          className="mt-3 text-sm text-[var(--danger)]"
          data-testid="ai-rebuild-error"
        >
          {error}
        </p>
      ) : null}
    </section>
  );
}
